import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { OCAColors, Typography, Spacing, Shadow, Gradient, Animation } from '../../theme/colors'

export const LoginPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [isPressed, setIsPressed] = useState(false)
  const [showError, setShowError] = useState(false)

  const { login, isAuthenticated } = useAuth()
  const navigate = useNavigate()

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/devices')
    }
  }, [isAuthenticated, navigate])

  // Auto-dismiss error toast
  React.useEffect(() => {
    if (error) {
      setShowError(true)
      const timer = setTimeout(() => {
        setShowError(false)
        setTimeout(() => setError(''), 300) // Clear after fade out
      }, 4000)
      return () => clearTimeout(timer)
    }
  }, [error])

  const handleLogin = async () => {
    setError('')
    setIsLoading(true)

    try {
      await login()
      // Auth0 will handle the redirect
      // Reset loading state after login initiates (webview or redirect)
      setIsLoading(false)
    } catch (error) {
      setError('Login failed. Please try again.')
      setIsLoading(false)
    }
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      padding: Spacing.xl,
      background: Gradient.primarySubtle,
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Error Toast */}
      {error && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '20px',
          right: '20px',
          padding: Spacing.md,
          backgroundColor: OCAColors.danger,
          color: OCAColors.background,
          borderRadius: '12px',
          boxShadow: Shadow.lg,
          fontSize: Typography.fontSize.sm,
          fontWeight: Typography.fontWeight.medium,
          zIndex: 1000,
          transform: showError ? 'translateY(0)' : 'translateY(-120%)',
          opacity: showError ? 1 : 0,
          transition: `all ${Animation.normal} ${Animation.easeOut}`,
          display: 'flex',
          alignItems: 'center',
          gap: Spacing.sm,
        }}>
          <span style={{ fontSize: '18px' }}>⚠️</span>
          {error}
        </div>
      )}

      {/* Main Content */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: '340px',
        animation: 'fadeIn 0.6s ease-out',
      }}>
        {/* Logo */}
        <div style={{
          textAlign: 'center',
          marginBottom: Spacing.xxl,
        }}>
          <img
            src="/omniiq_logo.png"
            alt="Omni IQ"
            style={{
              width: '140px',
              height: '140px',
              marginBottom: Spacing.lg,
              objectFit: 'contain',
              filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.08))',
            }}
          />
          <h1 style={{
            fontSize: Typography.fontSize['3xl'],
            fontWeight: Typography.fontWeight.bold,
            color: OCAColors.label,
            margin: 0,
            letterSpacing: '-0.5px',
          }}>
            OMNI IQ
          </h1>
        </div>

        {/* Login Button */}
        <button
          onClick={handleLogin}
          disabled={isLoading}
          onTouchStart={() => !isLoading && setIsPressed(true)}
          onTouchEnd={() => setIsPressed(false)}
          onMouseDown={() => !isLoading && setIsPressed(true)}
          onMouseUp={() => setIsPressed(false)}
          onMouseLeave={() => setIsPressed(false)}
          style={{
            width: '100%',
            padding: `${Spacing.lg} ${Spacing.xl}`,
            fontSize: Typography.fontSize.lg,
            fontWeight: Typography.fontWeight.semibold,
            color: OCAColors.background,
            background: isLoading ? OCAColors.gray400 : Gradient.primary,
            border: 'none',
            borderRadius: '16px',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            transition: `all ${Animation.normal} ${Animation.spring}`,
            minHeight: '52px',
            boxShadow: isLoading ? Shadow.sm : (isPressed ? Shadow.md : Shadow.button),
            marginBottom: Spacing.lg,
            transform: isPressed && !isLoading ? Animation.scaleDown : 'scale(1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: Spacing.sm,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {isLoading ? (
            <>
              <div style={{
                width: '20px',
                height: '20px',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }} />
              Connecting...
            </>
          ) : (
            <>
              <span style={{ fontSize: '20px' }}>🔐</span>
              Sign In
            </>
          )}
        </button>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
